import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  CardTypeEnum,
  PinwheelCardInput,
  PinwheelExperienceInput,
} from '@varo/graphql-schema-types';
import backgroundTextureLavender from 'assets/images/vxl/component/productIntroScreen/background-texture-lavender.png';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import BillManagerErrorScreen from 'features/billManager/components/BillManagerErrorScreen/BillManagerErrorScreen';
import { logErrorEvent } from 'features/billManager/helpers/logErrorEvent';
// TODO: Move UnfreezeCardsBottomSheet to shared/patterns/ — it is generic but currently
// lives in features/billSwitch/ and carries billSwitch-specific analytics internally.
import UnfreezeCardsBottomSheet from 'features/billSwitch/components/UnfreezeCardsBottomSheet/UnfreezeCardsBottomSheet';
import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ListItemGroup from 'shared/components/vxl/ListItemGroup/ListItemGroup';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { computeBillsTotal } from 'shared/helpers/computeBillsTotal/computeBillsTotal';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { useFetchCardsWithLockStatus } from 'shared/hooks/useFetchCardsWithLockStatus/useFetchCardsWithLockStatus';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { GlobalUIContext } from 'shared/providers/globalUIProvider/GlobalUIContextProvider/GlobalUIContextProvider';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const SOURCE_NAME = 'bill_manager_intro';

const FEATURE_BULLET_ITEMS = [
  { key: 'calendar', iconKey: 'CALENDAR' },
  { key: 'cancel', iconKey: 'SPARKLE' },
  { key: 'alerts', iconKey: 'NOTIFICATION' },
] as const;

const BillManagerProductIntroScreen = () => {
  const { defaultTitle, defaultMessage, retryButtonTitle, doneButtonTitle } =
    useDefaultErrorStrings();

  const { t: translatedText } = useTranslation('billManager', {
    keyPrefix: 'productIntro',
  });

  const { segmentClient } = useAnalyticsConfiguration();
  const { top } = useSafeAreaInsets();
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();
  const { setSnackBarMessage } = useContext(GlobalUIContext);

  const router = useRouter();

  const {
    cards,
    hasCards,
    hasLockedCards,
    analyticsMetadata: { isDebitLocked, isBelieveLocked },
    cardsError,
    cardsFetching,
    refetchCards,
  } = useFetchCardsWithLockStatus();

  const hasActiveBelieveCard = cards?.some(
    (card) => card.type === CardTypeEnum.ChargeCard,
  );

  const cardOptions = [
    PinwheelCardInput.Debit,
    ...(hasActiveBelieveCard ? [PinwheelCardInput.Believe] : []),
  ];

  const canLaunchPinwheel = hasCards && !hasLockedCards;

  useViewDidFocusLogScreenAnalytics({
    name: 'Screen Viewed',
    properties: {
      screen_name: 'Bill Manager Intro',
      category: 'bill_manager',
    },
  });

  useEffect(() => {
    if (cardsError) {
      logErrorEvent({
        segmentClient,
        error_type: 'fetch_cards_error',
        error_step: 'product_intro',
      });
    }
  }, [cardsError, segmentClient]);

  const launchPinwheel = () => {
    if (!canLaunchPinwheel) {
      return;
    }
    initiatePinwheel({
      input: {
        experience: PinwheelExperienceInput.BillManager,
        cardOptions,
      },
      sourceName: SOURCE_NAME,
      onLinkTokenError: () => {
        logErrorEvent({
          segmentClient,
          error_type: 'link_token_error',
          error_step: 'pinwheel_launch',
        });
        setSnackBarMessage?.(translatedText('linkTokenError'));
      },
      onOutcome: ({ sessionState }) => {
        const { billSwitches } = sessionState ?? {};
        if (billSwitches?.length) {
          const amount = computeBillsTotal(billSwitches);
          router.replace({
            pathname: '/bill-switch/funding',
            params: { amt: amount },
          });
        }
      },
    });
  };

  const hasError = Boolean(cardsError);
  const errorTitle = cardsError?.title ?? defaultTitle;
  const errorMessage = cardsError?.message ?? defaultMessage;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: '',
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryIcon={<Icon iconKey="BACK" iconSize="MD" />}
              transparent
            />
          ),
        }}
      />

      <ScreenTransitionOverlay
        isLoading={cardsFetching}
        LoadingComponent={
          <View style={{ paddingTop: top + theme.spacing.m }}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          hasError ? (
            <BillManagerErrorScreen
              messageHeadline={errorTitle}
              messageBody={errorMessage}
              primaryButton={{
                title: doneButtonTitle,
                onPress: navigateBack,
              }}
              secondaryButton={{
                title: retryButtonTitle,
                onPress: refetchCards,
              }}
            />
          ) : null
        }
      />

      {!cardsFetching ? (
        <ProductIntroScreen
          visual="BILL_MANAGER_A"
          headlineTextStyle="expressive"
          backgroundTexture={backgroundTextureLavender as ImageSourcePropType}
          headlineText={translatedText('title')}
          topPadding={70}
          primaryButton={{
            label: translatedText('cta'),
            onPress: () => {
              if (!canLaunchPinwheel) {
                bottomSheetRef?.current?.present();
              } else {
                launchPinwheel();
              }
            },
            isLoading: isPinwheelLoading,
            analyticsId: 'open_bill_manager',
            customAttributes: {
              source_name: SOURCE_NAME,
              button_name: 'open_bill_manager',
              category: 'bill_manager',
              debit_locked: isDebitLocked,
              believe_locked: isBelieveLocked,
            },
          }}
          bodyContent={
            <View style={styles.bodyContainer}>
              <View style={styles.bodyTextContainer}>
                <BodyBaseText numberOfLines={0}>
                  {translatedText('subtitle')}
                </BodyBaseText>
              </View>
              <ListItemGroup
                items={FEATURE_BULLET_ITEMS.map((item) => ({
                  primaryCopy: translatedText(`featureBullets.${item.key}`),
                  leadingAccessory: {
                    id: `item-${item.key}-icon`,
                    name: VXLComponentId.ICON_AVATAR,
                    props: { variant: 'MD', iconKey: item.iconKey },
                  },
                  showsChevron: false,
                }))}
              />
              <View style={styles.bodyTextContainer}>
                <BodyXSText numberOfLines={0} style={styles.legalText}>
                  {translatedText('legalText')}
                </BodyXSText>
              </View>
            </View>
          }
        />
      ) : null}
      <UnfreezeCardsBottomSheet
        ref={bottomSheetRef}
        useBelieveLanguage={false}
        hasLockedCards={hasLockedCards}
        hasCards={hasCards}
        cards={cards}
        analyticsMetadata={{ source_name: SOURCE_NAME }}
        onSuccess={() => {
          bottomSheetRef?.current?.dismiss();
          launchPinwheel();
        }}
        onDismiss={() => bottomSheetRef?.current?.dismiss()}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bodyContainer: {
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
  bodyTextContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  legalText: {
    color: theme.color.text.secondary,
  },
});

export default BillManagerProductIntroScreen;
